import React from 'react'
import { batch } from 'react-redux'
import { get } from 'lodash-es'
import {
  failedFetch,
  updateFetchRetry,
  setUnexpectedClientError,
} from '../../../features/ui/ui-slice'
import FetchError from './fetch-error'
import { goToMyAccount } from '../navigate'
import { errorCodes } from 'universal/error-codes'
import { errorTypes } from 'universal/error-types'
import { getNetworkStatus } from 'utils/utils'
import { getQueryParam } from 'utils/query'
import Retry from '@/screens/error/Retry'
import RetryError from '@/screens/error/RetryError'
import Denied from '@/screens/error/Denied'
import { PayrollPartnerError } from '@/screens/error/PayrollPartnerError'
import UnexpectedError from '@/screens/error/UnexpectedError'
import { SessionExpired } from 'components/error'
import { ERROR_PAGE_TYPES } from '@/constants/flow'
import { logActivityWithLastImpression } from 'utils/fpti'
import { isPayrollPartnerVariantOrIntent } from 'utils/flow'
import RequestBlocked from '../../components/error/RequestBlocked'

const { 
  VALIDATION_ERROR,
  NOT_FOUND_ERROR,
  FORBIDDEN_ERROR
} = errorTypes

/**
 * New flow error handler
 */
export function handleFailedFetch(err, options, callback) {
  const { fetchName, dispatch } = options || {}
  try {
    if (err.type && err.code === errorCodes.ACCESS_NOT_ALLOWED) {
      goToMyAccount()
      return
    }
    // Handle all error types just in case if there are unexpected
    // error causing spinner to keep loading
    const { type, code, message, reason, sysData, debugId } = err
    batch(() => {
      dispatch(
        failedFetch({
          fetchName,
          error: { type, code, message, reason, debugId },
          sysData,
        }),
      )

      if (callback) {
        callback(err, options)
        return
      }
    })
  } catch (runtimeErr) {
    dispatch(setUnexpectedClientError({ fetchName, error: runtimeErr }))
  }
}

export function handleUnexpectedError(err, options) {
  const { fetchName, getState, dispatch, retryAction, fetchRetried } = options
  const isOffline = !getNetworkStatus()

  if (err instanceof FetchError) {
    if (fetchRetried && !isOffline) {
      dispatch(updateFetchRetry({ fetchName, fetchRetried: true, retryAction: null }))
    } else {
      // To pass isRetry flag as the third argument to redux action handler
      const retryActionWithFlag = async () => {
        await retryAction(dispatch, getState, true, { isRetry: true })
      }

      dispatch(
        updateFetchRetry({
          fetchName,
          fetchRetried: false,
          retryHandler: () => {
            dispatch(retryActionWithFlag)
          },
        }),
      )
    }
    return
  }
  throw err
}

function hasValidationErrorWithInvalidResourceID(type, issue) {
  // invalid card_id with valid format
  return type === VALIDATION_ERROR && issue === 'INVALID_RESOURCE_ID'
}

function hasNotFoundErrorWithGetAccessInstrumentError(type, issue) {
  // invalid card_id with invalid format
  return type === NOT_FOUND_ERROR && issue === 'GET_ACCESS_INSTRUMENT_ERROR'
}

function hasForbiddenErrorWithInvalidResourceID(type, issue) {
  // invalid card_id for the user
  return type === FORBIDDEN_ERROR && issue === "INVALID_RESOURCE_ID"
}

export function hasInvalidCardId(error) {
  const { type, details } = error || {}
  const issue = get(details, '[0].issue', '')

  return (
    hasValidationErrorWithInvalidResourceID(type, issue) ||
    hasNotFoundErrorWithGetAccessInstrumentError(type, issue) ||
    hasForbiddenErrorWithInvalidResourceID(type, issue)
  )
}

export const isStepUpAuthServiceFailed = () => {
  const failedBecause = getQueryParam('failedBecause')
  return !!failedBecause
}

export const getErrorHandlerData = ({
  isSessionExpired,
  loginUrl,
  isRequestBlocked,
  unexpectedClientError,
  retryFetchName,
  fetchRetried,
  retryHandler,
  flowName = '',
  errorPageProps = {} as any,
}) => {
  const {
    SESSION_EXPIRED,
    UNEXPECTED_CLIENT_ERROR,
    NO_INTERNET_ERROR,
    SERVER_RETRY_ERROR,
    UNEXPECTED_SERVER_ERROR,
    RISK_STEPUP_SERVICE_FAILURE,
    REQUEST_BLOCKED,
  } = ERROR_PAGE_TYPES

  const isOffline = !getNetworkStatus()

  let errorContent
  let errorType = ''
  let errorMessage = ''

  if (isSessionExpired) {
    errorType = SESSION_EXPIRED
    errorContent = <SessionExpired loginUrl={loginUrl} />
  } else if (isRequestBlocked) {
    errorType = REQUEST_BLOCKED
    errorContent = <RequestBlocked actionType={isRequestBlocked.actionType} {...errorPageProps} />
  } else if (unexpectedClientError) {
    errorType = UNEXPECTED_CLIENT_ERROR
    const { fetchName: actionName, error } = unexpectedClientError
    errorContent = <UnexpectedError actionName={actionName} error={error} />
  } else if (retryFetchName) {
    errorMessage = `Failed fetch, fetch name: ${retryFetchName}`
    let RetryPage = Retry
    if (isOffline) {
      errorType = NO_INTERNET_ERROR
    } else if (fetchRetried) {
      errorType = SERVER_RETRY_ERROR
      RetryPage = RetryError as any
    } else {
      errorType = UNEXPECTED_SERVER_ERROR
    }

    errorContent = (
      <RetryPage
        {...errorPageProps}
        errorType={errorType}
        errorMessage={errorMessage}
        fetchingState={{
          fetchRetry: {
            actionType: retryFetchName,
            retryHandler,
          },
        }}
        isOffline={isOffline}
        flowName={flowName}
      />
    )
  } else if (isStepUpAuthServiceFailed()) {
    errorType = RISK_STEPUP_SERVICE_FAILURE
    const { errorCode } = errorPageProps
    logActivityWithLastImpression(`${flowName}:stepUpAuth`, {
      errorType,
    })
    errorContent = (
      <Denied
        errorType={errorType}
        errorCode={`${errorCode ? errorCode : ''}${getQueryParam('failedBecause')}`}
        flowName={flowName}
        actionType={undefined}
      />
    )
  }

  // We want to cover most of the generic errors for UKG.
  if (isPayrollPartnerVariantOrIntent() && errorType) {
    errorContent = (
      <PayrollPartnerError flowName={flowName} errorType={errorType} errorMessage={errorMessage} />
    )
  }

  return {
    errorType,
    errorContent,
  }
}
